import { useEffect, useRef, useState, type ReactElement } from 'react';
import { trackFailed } from '../../../../../../shared-web/track';
import { apiErrorCode, apiFailureReason, emberApi, isAuthRequired, type GroupCheck, type GroupMemberPostKind, type GroupPage } from '../../api';
import { PageFailure } from '../../components/PageFailure';
import { PageLoading } from '../../components/PageLoading';
import { toast } from '../../../../design-system/components';
import { goBack, goGroup, navigate } from '../../router';
import { useKeptAcrossReload } from '../../use-kept-across-reload';
import { CheckView, CheckingVeil, CLOSE, GROUP_LIMITS, GroupBar, GroupField, HeldNote, LOCK, MIC_SMALL, PostingBand, refusalOf, useDictation, type Refusal } from './group-parts';

interface Words { question: string; body: string }

/**
 * Putting a post up — `/groups/:groupId/new?kind=question|share[&draft=1]` (2026-11-03 founder; mock Compose / Check).
 * A question is the question and a little background; a share is one piece of text. Typing first, a mic in each field.
 * With `draft=1` (Ember's card in their conversation) the post Ember wrote up from what they said is put in the fields
 * (unless words of theirs were waiting) and goes up `via: 'draft'`. Next is Ember's look: nothing to change, it goes up
 * and they land on the group, the new post at its top; a change, "Ember suggests a couple of changes", theirs to take or
 * to change themselves. Words held back for the writer's own sake stay with them, with the lines to call.
 */
export function GroupNewPage({ groupId, search }: { groupId: string; search: URLSearchParams }): ReactElement {
  const fromDraft = search.get('draft') === '1';
  const path = `/groups/${encodeURIComponent(groupId)}`;
  const here = `${path}/new?${search.toString()}`;
  const [kind, setKind] = useState<GroupMemberPostKind>(search.get('kind') === 'share' ? 'share' : 'question');
  const [group, setGroup] = useState<GroupPage['group'] | null>(null);
  const [failed, setFailed] = useState<'' | 'not_open' | 'load_failed'>('');
  const [words, setWords] = useState<Words>({ question: '', body: '' });
  const [drafting, setDrafting] = useState(fromDraft);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState<GroupCheck | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const dictation = useDictation();

  // What they typed waits for them across a reload or an accidental close (a long background is not lost); posting lets
  // it go. One copy per way in: a question, a share, or Ember's draft (whose kind is the draft's).
  const keptKey = `ember.group-new.${groupId}.${fromDraft ? 'draft' : kind}`;
  const typedSomething = Boolean(words.question.trim() || words.body.trim());
  const kept = useRef(false);
  useKeptAcrossReload(keptKey, typedSomething ? JSON.stringify({ kind, ...words }) : '', (raw) => {
    try {
      const k = JSON.parse(raw) as Partial<Words> & { kind?: string };
      kept.current = true;
      if (fromDraft && (k.kind === 'question' || k.kind === 'share')) setKind(k.kind);
      setWords({ question: String(k.question || ''), body: String(k.body || '') });
    } catch { /* nothing to put back */ }
  });

  const toAuth = (): void => navigate(`/auth?returnTo=${encodeURIComponent(here)}`, { replace: true });

  useEffect(() => {
    emberApi.group(groupId)
      .then((r) => setGroup(r.group))
      .catch((err: unknown) => {
        if (isAuthRequired(err)) { toAuth(); return; }
        if (apiErrorCode(err) === 'not_open') { setFailed('not_open'); return; }
        trackFailed('group_page', apiFailureReason(err), { match_id: groupId, state: 'new' });
        setFailed('load_failed');
      });
    if (!fromDraft) return;
    emberApi.groupDraft(groupId)
      .then((d) => {
        if (kept.current) return;
        setKind(d.kind === 'share' ? 'share' : 'question');
        setWords({ question: d.question || '', body: d.body || '' });
      })
      .catch((err: unknown) => {
        if (isAuthRequired(err)) { toAuth(); return; }
        if (apiErrorCode(err) === 'not_open') { setFailed('not_open'); return; }
        trackFailed('group_draft', apiFailureReason(err), { match_id: groupId });
      })
      .finally(() => setDrafting(false));
  }, [groupId]);

  if (failed === 'not_open') return <PageFailure title="This group isn’t open to you" detail="Groups open up based on what you’ve talked about with Ember." logState="not_open" />;
  if (failed) return <PageFailure title="Couldn’t open this page" logState="load_failed" retry />;
  if (!group) return <PageLoading />;

  const question = words.question.trim().replace(/\s+/g, ' ');
  const body = words.body.trim();
  const ready = kind === 'question' ? Boolean(question && body) : Boolean(body);
  const fields: Record<string, string> = kind === 'question' ? { question: 'Your question', body: 'Your background' } : { body: 'Your post' };
  const set = (field: keyof Words) => (value: string): void => {
    setWords((w) => ({ ...w, [field]: value }));
    if (refusal && 'field' in refusal && refusal.field === field) setRefusal(null);
  };

  const refused = (err: unknown): void => {
    if (isAuthRequired(err)) { toAuth(); return; }
    if (apiErrorCode(err) === 'not_open') { setFailed('not_open'); return; }
    setCheck(null);
    setRefusal(refusalOf(err, fields));
  };

  const post = async (out: Words): Promise<void> => {
    await emberApi.postToGroup(groupId, { kind, question: kind === 'question' ? out.question : '', body: out.body, via: fromDraft ? 'draft' : 'page' });
    try { window.sessionStorage.removeItem(keptKey); } catch { /* nothing kept */ }
    // Up: the group, its top, where the new post is (2026-11-03 founder), with a light word that it went.
    toast.success(kind === 'question' ? 'Posted. You’ll hear when someone answers.' : 'Posted. You’ll hear when someone replies.');
    goGroup(groupId);
  };

  const next = async (): Promise<void> => {
    if (busy || !ready) return;
    dictation.stop();
    setBusy(true); setChecking(true); setRefusal(null);
    try {
      const out = await emberApi.checkGroupWords(groupId, { kind, ...(kind === 'question' ? { question } : {}), body });
      if (out.changes.length && out.suggested) { setCheck(out); return; }
      await post({ question, body });
    } catch (err) {
      trackFailed('group_check', apiFailureReason(err), { match_id: groupId, state: kind });
      refused(err);
    } finally {
      setBusy(false); setChecking(false);
    }
  };

  const take = async (): Promise<void> => {
    if (busy || !check?.suggested) return;
    const s = check.suggested;
    const out = { question: s.question ?? question, body: s.body ?? body };
    setBusy(true);
    try {
      await post(out);
    } catch (err) {
      trackFailed('group_post', apiFailureReason(err), { match_id: groupId, state: kind });
      setWords(out);
      refused(err);
    } finally {
      setBusy(false);
    }
  };

  if (check) {
    return (
      <CheckView
        kind={kind}
        check={check}
        busy={busy}
        notice=""
        onTake={() => { void take(); }}
        onEdit={() => {
          const s = check.suggested ?? {};
          setWords({ question: s.question ?? words.question, body: s.body ?? words.body });
          setCheck(null);
        }}
      />
    );
  }

  const refusedOf = (field: keyof Words): string => (refusal && 'field' in refusal && refusal.field === field ? refusal.message : '');
  const placeholder = drafting ? 'Ember is writing it up…' : 'Start typing…';
  return (
    <main className="nf-page gr-page" data-log-seen={`group_new_${kind}`} data-log-state={fromDraft ? 'draft' : 'page'} data-log-match-id={groupId}>
      {checking ? <CheckingVeil what="post" /> : null}
      <GroupBar
        to={path}
        title={kind === 'question' ? 'Ask a question' : 'Share what’s going on'}
        left={<button type="button" className="nf-iconbtn" aria-label="Close" data-log="group_new_close" data-log-state={kind} onClick={() => goBack(null, path)}>{CLOSE}</button>}
      />
      <div className="gr-compose">
        <PostingBand me={group.me} group={group.name} tone={kind === 'question' ? 'peach' : 'sage'} mark={kind === 'question' ? 'question' : 'heart'} />
        {kind === 'question' && (
          <GroupField
            id="group-question"
            label="Your question"
            value={words.question}
            onChange={set('question')}
            dictation={dictation}
            serif
            rows={2}
            maxLength={GROUP_LIMITS.QUESTION}
            placeholder={drafting ? placeholder : 'What would you like to ask?'}
            disabled={drafting}
            refused={refusedOf('question')}
          />
        )}
        <GroupField
          id="group-body"
          label={kind === 'question' ? 'A little background' : 'What’s going on'}
          guide={kind === 'question' ? (
            <div className="gr-guide">
              <span className="gr-guide-hand">what helps people answer</span>
              <ul className="gr-guide-list">
                <li>What happened</li>
                <li>Where things are now</li>
                <li>What you’re unsure about</li>
              </ul>
            </div>
          ) : (
            <div className="gr-guide is-sage">
              <span className="gr-guide-hand">no answer needed</span>
              <span className="gr-guide-line">Say it however it comes. People can still react or reply.</span>
            </div>
          )}
          value={words.body}
          onChange={set('body')}
          dictation={dictation}
          rows={7}
          maxLength={GROUP_LIMITS.BODY}
          placeholder={placeholder}
          disabled={drafting}
          refused={refusedOf('body')}
        />
        <ul className="gr-notes">
          <li>{LOCK}<span>No names needed. Only the group sees it.</span></li>
          {dictation.supported && <li>{MIC_SMALL}<span>Rather talk? Tap the mic and say it.</span></li>}
        </ul>
        {refusal && 'held' in refusal && <HeldNote resources={refusal.held} what="post" />}
      </div>
      <div className="nf-foot">
        {refusal && 'note' in refusal && <p className="nf-foot-note is-missing" role="alert">{refusal.note}</p>}
        <p className="nf-foot-note">Ember takes a quick look before it goes up.</p>
        <button type="button" className="nf-cta is-block" disabled={busy || drafting || !ready} data-log="group_check" data-log-state={kind} data-log-match-id={groupId} onClick={() => { void next(); }}>
          {busy ? 'Taking a look…' : 'Next'}
        </button>
      </div>
    </main>
  );
}
